import React, { useState, useEffect, useCallback } from 'react';
import { 
    Box, 
    Alert, 
    Button, 
    TextField, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Typography, 
    Chip, 
    CircularProgress, 
    Paper, 
    TablePagination,
    InputAdornment
} from '@mui/material';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { convertCsvToExportFormat } from '../../../utils/exportUtils';
import { LoginService } from '../../../api/services/loginService';
import ReplayIcon from '@mui/icons-material/Replay';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardFilterSelectSx, standardFilterInputSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

const AUDIT_ACTION_TYPES = [
    'All',
    'LOGIN_SUCCESS',
    'LOGIN_FAILURE',
    'LOGOUT',
    'PASSWORD_CHANGE',
    'MFA_VERIFIED',
    'MFA_ENABLED',
    'MFA_DISABLED',
    'SESSION_TERMINATED',
    'ACCOUNT_LOCKED',
];

export default function AuditLogsTab({ setNotification }) {
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditTotal, setAuditTotal] = useState(0);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState('');
    const [auditPage, setAuditPage] = useState(0);
    const [auditRowsPerPage, setAuditRowsPerPage] = useState(20);

    const [auditDateFrom, setAuditDateFrom] = useState('');
    const [auditDateTo, setAuditDateTo] = useState('');
    const [auditUserFilter, setAuditUserFilter] = useState('');
    const [auditActionFilter, setAuditActionFilter] = useState('All');
    
    const [exportLoading, setExportLoading] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const fetchAuditLogs = useCallback(async () => {
        setAuditLoading(true);
        setAuditError('');
        try {
            const data = await LoginService.getAuditLogs({
                page: auditPage,
                limit: auditRowsPerPage,

                dateFrom: auditDateFrom,
                dateTo: auditDateTo,
                user: auditUserFilter,
                action: auditActionFilter,
            });
            const logsList = data?.data?.logs || data?.data || data?.logs || [];
            setAuditLogs(Array.isArray(logsList) ? logsList : []);
            setAuditTotal(extractTotalCount(data, logsList));
        } catch (err) {
            setAuditError(err?.response?.data?.message || err.message || 'Failed to fetch audit logs');
        } finally {
            setAuditLoading(false);
        }
    }, [auditPage, auditRowsPerPage, auditDateFrom, auditDateTo, auditUserFilter, auditActionFilter]);

    useEffect(() => {
        fetchAuditLogs();
    }, [fetchAuditLogs]);

    useEffect(() => {
        setAuditPage(0);
    }, [auditDateFrom, auditDateTo, auditUserFilter, auditActionFilter]);

    const handleResetFilters = () => {

        setAuditDateFrom('');
        setAuditDateTo('');
        setAuditUserFilter('');
        setAuditActionFilter('All');
        setAuditPage(0);
    };

    const handleExportLogs = async (format) => {
        setExportModalOpen(false);
        setExportLoading(true);
        try {
            // Use the export API and request CSV format
            const response = await LoginService.exportAuditLogs({
                search: auditSearch,
                dateFrom: auditDateFrom,
                dateTo: auditDateTo,
                user: auditUserFilter,
                action: auditActionFilter,
                format: 'csv' // Always pass CSV as query param
            });
            
            // Convert the Blob response to text (CSV string)
            const csvText = await response.data.text();
            
            if (!csvText || csvText.trim() === '') {
                setNotification({ open: true, message: 'No logs available to export', severity: 'warning' });
                return;
            }
            
            const filenameBase = `audit-logs-${new Date().toISOString().split('T')[0]}`;
            
            // convertCsvToExportFormat parses the CSV string and outputs the requested format (CSV/Excel/PDF)
            await convertCsvToExportFormat(csvText, format, filenameBase);
            setNotification({ open: true, message: 'Audit logs exported successfully', severity: 'success' });
        } catch (err) {
            setNotification({ open: true, message: err?.response?.data?.message || err.message || 'Failed to export logs', severity: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    return (
        <Box>
            {auditError && <Alert severity="error" sx={{ mb: 2 }}>{auditError}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Audit Logs</Typography>
                <Button 
                    variant="outlined" 
                    startIcon={exportLoading ? <CircularProgress size={18} color="inherit" /> : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} 
                    onClick={() => setExportModalOpen(true)}
                    disabled={exportLoading}
                    sx={standardSecondaryButtonSx}
                >
                    Export Logs
                </Button>
            </Box>

            <ExportFormatModal 
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                onExport={handleExportLogs}
                isExporting={exportLoading}
                title="Export Audit Logs"
            />

            {/* Filters */}
            <Box sx={standardFilterContainerSx}>
                <TextField
                    size="small"
                    placeholder="Search name or email..."
                    value={auditUserFilter}
                    onChange={(e) => setAuditUserFilter(e.target.value)}
                    sx={standardSearchInputSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small"
                        type="date"
                        value={auditDateFrom}
                        onChange={(e) => setAuditDateFrom(e.target.value)}
                        sx={{ width: 150, ...standardFilterInputSx }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        value={auditDateTo}
                        onChange={(e) => setAuditDateTo(e.target.value)}
                        sx={{ width: 150, ...standardFilterInputSx }}
                    />
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <Select
                            value={auditActionFilter}
                            displayEmpty
                            onChange={(e) => setAuditActionFilter(e.target.value)}
                            sx={standardFilterSelectSx}
                        >
                            {AUDIT_ACTION_TYPES.map(type => (
                                <MenuItem key={type} value={type}>{type === 'All' ? 'All Actions' : type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {(auditDateFrom !== '' || auditDateTo !== '' || auditUserFilter !== '' || auditActionFilter !== 'All') && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={handleResetFilters}>Reset</Button>
                    )}
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Timestamp</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Action</TableCell>
                            <TableCell>IP Address</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {auditLoading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : auditLogs.length > 0 ? (
                            auditLogs.map((log) => (
                                <TableRow key={log.id || log._id}>
                                    <TableCell>{log.timestamp ? new Date(log.timestamp).toLocaleString() : 'N/A'}</TableCell>
                                    <TableCell>{log.user?.name || 'N/A'}</TableCell>
                                    <TableCell>{log.user?.email || log.email || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={log.action} 
                                            size="small" 
                                            variant="outlined"
                                            color={log.action?.includes('FAILURE') || log.action?.includes('LOCKED') ? 'error' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>{log.ip || log.ipAddress || 'N/A'}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                    <Typography variant="body2" color="text.secondary">No audit logs found.</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {auditTotal > 0 && (
                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100]}
                        component="div"
                        count={auditTotal}
                        rowsPerPage={auditRowsPerPage}
                        page={auditPage}
                        onPageChange={(e, newPage) => setAuditPage(newPage)}
                        onRowsPerPageChange={(e) => { setAuditRowsPerPage(parseInt(e.target.value, 10)); setAuditPage(0); }}
                    />
                )}
            </TableContainer>
        </Box>
    );
}
